import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { BuilderService } from '../../../services/builder.service';
import { FieldType } from '../field-palette/field-palette.component';

@Component({
	selector: 'nx-ahs-builder-canvas',
	templateUrl: './builder-canvas.component.html',
	styleUrl: './builder-canvas.component.scss',
	standalone: false
})
export class BuilderCanvasComponent {
	@Input() fields: FormlyFieldConfig[] = [];
	@Input() selectedFieldPath: number[] = [];
	@Output() fieldSelected = new EventEmitter<{ field: FormlyFieldConfig; path: number[] }>();
	@Output() fieldUpdated = new EventEmitter<{ field: FormlyFieldConfig; path: number[] }>();
	@Output() fieldDropped = new EventEmitter<void>();
	@Output() fieldsChanged = new EventEmitter<void>();

	constructor(private builderService: BuilderService) {}

	get hasFields(): boolean {
		return this.fields && this.fields.length > 0;
	}

	isFieldSelected(path: number[]): boolean {
		return JSON.stringify(path) === JSON.stringify(this.selectedFieldPath);
	}

	onFieldSelected(field: FormlyFieldConfig, path: number[]): void {
		this.fieldSelected.emit({ field, path });
	}

	onFieldUpdated(index: number, updatedField: FormlyFieldConfig): void {
		this.fields[index] = updatedField;
		this.fieldUpdated.emit({ field: updatedField, path: [index] });
		this.fieldsChanged.emit();
	}

	onDrop(event: CdkDragDrop<FormlyFieldConfig[]>): void {
		if (event.previousContainer === event.container) {
			// Reordering within the same list
			moveItemInArray(this.fields, event.previousIndex, event.currentIndex);
			this.fieldDropped.emit();
			this.fieldsChanged.emit();
		} else {
			// Adding new field from palette - show dialog first
			const fieldType = event.item.data as FieldType;
			const existingKeys = this.builderService.getAllFieldKeys(this.fields);
			const targetIndex = event.currentIndex;

			this.builderService.openAddFieldDialog(fieldType.type, existingKeys).subscribe((newField) => {
				if (newField) {
					this.fields.splice(targetIndex, 0, newField);
					this.fieldDropped.emit();
					this.fieldsChanged.emit();
				}
			});
		}
	}

	onDeleteField(index: number): void {
		this.fields.splice(index, 1);
		this.fieldsChanged.emit();
		this.fieldSelected.emit({ field: null, path: [] });
	}

	onDuplicateField(index: number): void {
		const field = this.fields[index];
		const duplicated = JSON.parse(JSON.stringify(field));
		// Update key if exists to avoid duplicates
		if (duplicated.key) {
			duplicated.key = `${duplicated.key}_copy`;
		}
		this.fields.splice(index + 1, 0, duplicated);
		this.fieldsChanged.emit();
	}

	onMoveUp(index: number): void {
		if (index > 0) {
			moveItemInArray(this.fields, index, index - 1);
			this.selectedFieldPath = [index - 1];
			this.fieldsChanged.emit();
		}
	}

	onMoveDown(index: number): void {
		if (index < this.fields.length - 1) {
			moveItemInArray(this.fields, index, index + 1);
			this.selectedFieldPath = [index + 1];
			this.fieldsChanged.emit();
		}
	}
}
